import React, { useState, useEffect, useRef } from 'react'
import { X, Building2, Mail, User, MessageSquare, FileText, Loader, CheckCircle, ExternalLink, Copy, Search, Clock, ShieldCheck, AlertTriangle } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface RequestAccessModalProps {
  isOpen: boolean
  onClose: () => void
}

const RequestAccessModal: React.FC<RequestAccessModalProps> = ({ isOpen, onClose }) => {
  // Don't render anything if modal is not open
  if (!isOpen) {
    return null
  }

  const { currentTenant } = useTenant()

  const [formData, setFormData] = useState({
    email: '',
    company: '',
    name: '',
    role: '',
    reason: 'Access request submitted',
    targets: ['General Access'] as string[]
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [requestId, setRequestId] = useState<string>('')
  const [autoApproved, setAutoApproved] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [copySuccess, setCopySuccess] = useState(false)
  const [showStatusTracker, setShowStatusTracker] = useState(false)
  const [statusRequestId, setStatusRequestId] = useState('')
  const [statusData, setStatusData] = useState<any>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusError, setStatusError] = useState('')

  // Store timeout ID for copy success message
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const documentTypes = [
    'SOC 2 Type II Report',
    'ISO 27001 Certificate',
    'Penetration Test Summary',
    'Security Policies',
    'Data Processing Agreement',
    'Architecture Documentation',
    'CAIQ Response',
    'Security Questionnaire Responses'
  ]

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.company || formData.company.length < 2) {
      newErrors.company = 'Company name is required (minimum 2 characters)'
    }

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Full name is required (minimum 2 characters)'
    }

    if (!formData.role) {
      newErrors.role = 'Please select your role'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleTargetToggle = (target: string) => {
    setFormData(prev => ({
      ...prev,
      targets: prev.targets.includes(target)
        ? prev.targets.filter(t => t !== target)
        : [...prev.targets, target]
    }))

    // Clear targets error when user selects something
    if (errors.targets) {
      setErrors(prev => ({ ...prev, targets: '' }))
    }
  }

  const copyRequestId = async () => {
    try {
      await navigator.clipboard.writeText(requestId)
      setCopySuccess(true)
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
      copyTimeoutRef.current = setTimeout(() => {
        setCopySuccess(false)
        copyTimeoutRef.current = null
      }, 2000)
    } catch (err) {
      console.error('Failed to copy request ID:', err)
    }
  }

  const checkRequestStatus = async () => {
    if (!statusRequestId.trim()) {
      setStatusError('Please enter a request ID')
      return
    }

    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    if (!tenantId) {
      setStatusError('Tenant configuration not found. Please refresh the page.')
      return
    }

    setStatusLoading(true)
    setStatusError('')
    setStatusData(null)

    try {
      const response = await fetch(`/api/request-status?id=${encodeURIComponent(statusRequestId.trim())}&tenantId=${encodeURIComponent(tenantId)}`)

      if (response.ok) {
        const data = await response.json()
        setStatusData(data)
      } else if (response.status === 404) {
        setStatusError('Request ID not found. Please check your ID and try again.')
      } else {
        setStatusError('Failed to check status. Please try again later.')
      }
    } catch (error) {
      console.error('Error checking status:', error)
      setStatusError('Failed to check status. Please check your internet connection.')
    }

    setStatusLoading(false)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />
      case 'approved':
        return <ShieldCheck className="w-5 h-5 text-green-600" />
      case 'rejected':
        return <AlertTriangle className="w-5 h-5 text-red-600" />
      default:
        return <Clock className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-800 bg-yellow-100'
      case 'approved':
        return 'text-green-800 bg-green-100'
      case 'rejected':
        return 'text-red-800 bg-red-100'
      default:
        return 'text-gray-800 bg-gray-100'
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    // Get tenant ID from sessionStorage (set by branch deploy config)
    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    if (!tenantId) {
      alert('Tenant configuration not found. Please refresh the page.')
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('/api/request-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          tenantId
        })
      })

      if (response.ok) {
        const result = await response.json()
        setRequestId(result.request_id)
        setAutoApproved(result.autoApproved || false)
        setSubmitted(true)
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to submit request' }))
        console.error('Failed to submit request:', errorData)
        alert(`Failed to submit request: ${errorData.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error submitting request:', error)
      alert('Failed to submit request. Please check your internet connection and try again.')
    }

    setIsSubmitting(false)
  }

  if (showStatusTracker) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Search className="w-5 h-5 mr-2" />
              Check Request Status
            </h2>
            <button
              onClick={() => {
                setShowStatusTracker(false)
                setStatusData(null)
                setStatusError('')
                setStatusRequestId('')
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="statusRequestId" className="block text-sm font-medium text-gray-700 mb-2">
                Request ID
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  id="statusRequestId"
                  value={statusRequestId}
                  onChange={(e) => setStatusRequestId(e.target.value)}
                  placeholder="Enter your request ID"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={checkRequestStatus}
                  disabled={statusLoading}
                  className="px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {statusLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {statusError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{statusError}</p>
              </div>
            )}

            {statusData && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(statusData.status)}
                    <span className="ml-2 font-medium text-gray-900">Request Status</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(statusData.status)}`}>
                    {statusData.status.charAt(0).toUpperCase() + statusData.status.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Company</p>
                    <p className="font-medium text-gray-900">{statusData.company}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Submitted</p>
                    <p className="font-medium text-gray-900">{new Date(statusData.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {statusData.approvedAt && (
                  <div className="text-sm">
                    <p className="text-gray-500">Approved</p>
                    <p className="font-medium text-gray-900">{new Date(statusData.approvedAt).toLocaleDateString()}</p>
                    {statusData.approvedBy && (
                      <p className="text-gray-500">by {statusData.approvedBy}</p>
                    )}
                  </div>
                )}

                {statusData.expiresAt && (
                  <div className="text-sm">
                    <p className="text-gray-500">Access Expires</p>
                    <p className="font-medium text-gray-900">{new Date(statusData.expiresAt).toLocaleDateString()}</p>
                  </div>
                )}

                <div className="text-sm">
                  <p className="text-gray-500 mb-1">Documents Requested</p>
                  <ul className="text-gray-900 space-y-1">
                    {(statusData.targets || []).map((doc: string, index: number) => (
                      <li key={index} className="text-xs">• {doc}</li>
                    ))}
                  </ul>
                </div>

                {statusData.status === 'approved' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800 font-medium">Access Granted!</p>
                    <p className="text-xs text-green-700 mt-1">
                      You can now access the evidence library. Check your email for instructions.
                    </p>
                  </div>
                )}

                {statusData.status === 'rejected' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-medium">Request Declined</p>
                    <p className="text-xs text-red-700 mt-1">
                      Contact support if you have questions about this decision.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowStatusTracker(false)
                  setStatusData(null)
                  setStatusError('')
                  setStatusRequestId('')
                }}
                className="flex-1 btn-secondary"
              >
                Close
              </button>
              {statusData?.status === 'approved' && (
                <button
                  onClick={() => window.open('/evidence', '_blank')}
                  className="flex-1 btn-primary flex items-center justify-center"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Access Library
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-lg w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {autoApproved ? 'Access Approved!' : 'Request Submitted Successfully!'}
            </h3>
            <p className="text-gray-600 mb-4">
              {autoApproved
                ? '🎉 Your access has been automatically approved! You can login now.'
                : 'Your access request has been received and is being reviewed by our security team.'}
            </p>

            {/* Request ID */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Request ID</p>
                  <p className="text-xs text-gray-500 font-mono">{requestId}</p>
                </div>
                <button
                  onClick={copyRequestId}
                  className="text-blue-600 hover:text-blue-700 p-1"
                  title="Copy request ID"
                >
                  {copySuccess ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Next Steps */}
            <div className="text-left mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">What happens next?</h4>
              {autoApproved ? (
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Ready to Login!</p>
                      <p className="text-xs text-gray-600">Click "Go to Login" below to access the Trust Center</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Use "Verify with Code"</p>
                      <p className="text-xs text-gray-600">Enter your email and we'll send you a verification code</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-xs font-semibold text-blue-600">1</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email Confirmation</p>
                      <p className="text-xs text-gray-600">You'll receive an email confirmation within 15 minutes</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-xs font-semibold text-yellow-600">2</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Security Review</p>
                      <p className="text-xs text-gray-600">Our team will review your request within 1 business day</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-xs font-semibold text-green-600">3</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Access Instructions</p>
                      <p className="text-xs text-gray-600">If approved, you'll receive secure access credentials</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Request Summary */}
            <div className="text-left bg-blue-50 rounded-lg p-3 mb-6">
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Request Summary</h4>
              <p className="text-xs text-blue-700">
                Access requested for {formData.company}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Contact: {formData.email}
              </p>
            </div>

            <div className="flex space-x-3">
              {autoApproved ? (
                <>
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-semibold"
                  >
                    Go to Login →
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 btn-secondary"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => window.open('mailto:support@company.com?subject=Access Request ' + requestId, '_blank')}
                    className="flex-1 btn-secondary flex items-center justify-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Contact Support
                  </button>
                  <button
                    onClick={() => {
                      setSubmitted(false)
                      setShowStatusTracker(true)
                      setStatusRequestId(requestId)
                    }}
                    className="flex-1 btn-secondary flex items-center justify-center"
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Check Status
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 btn-primary"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Request Access</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="john@company.com"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.company ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Company Name"
                />
                {errors.company && (
                  <p className="mt-1 text-xs text-red-600">{errors.company}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="John Smith"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Role/Title
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.role ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Role</option>
                  <option value="security">Security/Compliance Officer</option>
                  <option value="legal">Legal Counsel</option>
                  <option value="procurement">Procurement</option>
                  <option value="sales">Sales/Business Development</option>
                  <option value="engineering">Engineering/IT</option>
                  <option value="other">Other</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-xs text-red-600">{errors.role}</p>
                )}
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowStatusTracker(true)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center mx-auto"
              >
                <Search className="w-4 h-4 mr-1" />
                Already submitted a request? Check status here
              </button>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RequestAccessModal